import React, { useEffect, useState } from "react";
import TopBarComponent from "../TopBarComponent";
import SidebarComponent from "../SidbarComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setSelectedScholarship,
  setRequirements,
} from "../../redux/slices/scholarshipSlice";
import { getUserId, getAuthHeaders } from '../../utils/auth';

import {
  User,
  Calendar,
  MapPin,
  GraduationCap,
  FileText,
  Clock,
  Search,
  Filter,
  X,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
} from "lucide-react";
import { FooterComponents } from "../FooterComponent";

import { useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function AbroadScholarship() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("applications");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(""); // New state for category filter
  const [scholarshipData, setScholarshipData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appliedScholarship, setAppliedScholarship] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [applicationToUpdate, setApplicationToUpdate] = useState(null);
  const [updateFormData, setUpdateFormData] = useState({});

  useEffect(() => {
    const fetchAppliedScholarships = async (userId) => {
      if (!userId) {
        setAppliedScholarship([]);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          process.env.REACT_APP_BASE_URL +
            `hec/abroad-scholarship-application/get-by-user?userId=${userId}`,
          { headers: getAuthHeaders() }
        );
        setAppliedScholarship(response.data || []);
      } catch (error) {
        console.error("Error fetching applied scholarships:", error);
        setAppliedScholarship([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppliedScholarships(getUserId());
  }, []);

  const filteredData = useMemo(() => {
    if (!appliedScholarship || !Array.isArray(appliedScholarship)) return [];
    if (!searchTerm) return appliedScholarship;

    return appliedScholarship.filter(
      (item) =>
        item?.educationProgram?.programName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item?.educationProgram?.universityName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item?.applicationStatus
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item?.currentLevel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.applyLevel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.educationProgram?.abroadScholarship?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, appliedScholarship]);

  const fetchScholarshipsByAcademicYear = async (academicYearId) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          `hec/abroad/find-scholarships?academicYear=${academicYearId}`
      );
      setScholarshipData(response.data || []);
    } catch (error) {
      setScholarshipData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScholarshipsByAcademicYear(new Date().getFullYear()-1);
  }, []);

  // Transform data for display
  const scholarships = useMemo(() => {
    if (!scholarshipData || !Array.isArray(scholarshipData)) return [];
    return scholarshipData
      .map((item) => {
        if (!item) return null;

        const startDate = item.startDate ? new Date(item.startDate) : null;
        const endDate = item.endDate ? new Date(item.endDate) : null;
        const today = new Date();

        // Determine status based on dates
        let status = "Open";
        if (endDate && endDate < today) {
          status = "Closed";
        } else if (endDate && (endDate - today) / (1000 * 60 * 60 * 24) < 7) {
          status = "Closing Soon";
        }

        // Count total requirements across all education programs
        const totalRequirements =
          item.educationPrograms?.reduce((total, program) => {
            return total + (program.requirements?.length || 0);
          }, 0) || 0;

        // Count education programs
        const programCount = item.educationPrograms?.length || 0;

        // Format deadline
        const deadline = endDate
          ? endDate
              .toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
              .toUpperCase()
          : "N/A";

        return {
          id: item.id,
          title: item.name,
          description: item.description,
          deadline: deadline,
          country: item.countryName || "Not specified",
          status: status,
          category: item.scholarshipType || "GOVERNMENT",
          startDate: startDate
            ? startDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "N/A",
          programCount: programCount,
          educationPrograms: item.educationPrograms || [],
          rawStartDate: startDate, // Keep raw date for validation
          rawEndDate: endDate, // Keep raw date for validation
        };
      })
      .filter(Boolean);
  }, [scholarshipData]);

  const filteredScholarships = useMemo(() => {
    if (!scholarships || !Array.isArray(scholarships)) return [];
    // Remove scholarships with status "Closed"
    let openScholarships = scholarships.filter(
      (scholarship) => scholarship.status !== "Closed"
    );

    // Apply category filter
    if (categoryFilter) {
      openScholarships = openScholarships.filter(
        (scholarship) => scholarship.category === categoryFilter
      );
    }

    // Apply search filter
    if (!searchTerm) return openScholarships;
    return openScholarships.filter(
      (scholarship) =>
        scholarship?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scholarship?.country
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        scholarship?.level?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [scholarshipData, searchTerm, categoryFilter]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getApplicationStatus = (status) => {
    switch (status) {
      case "RECEIVED":
        return "RECEIVED BY HEC";
      case "UNDER_REVIEW":
        return "ASSIGNED FOR REVIEW";
      case "SUBMITTED_TO_PARTENER":
        return "RECEIVED BY PARTNER";
      case "SUBMITTED_TO_UNIVERSITY":
        return "RECEIVED BY UNIVERSITY";
      case "GRANTED":
        return "GRANTED";
      case "REJECTED":
        return "NOT GRANTED";
      case "APPROVED":
        return "APPROVED";
      case "PENDING":
        return "PENDING";
      default:
        return "PENDING";
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case "RECEIVED":
      case "RECEIVED BY HEC":
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case "UNDER_REVIEW":
      case "UNDER REVIEW":
      case "ASSIGNED FOR REVIEW":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "SUBMITTED_TO_PARTENER":
      case "RECEIVED BY PARTNER":
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case "SUBMITTED_TO_UNIVERSITY":
      case "RECEIVED BY UNIVERSITY":
        return `${baseClasses} bg-indigo-100 text-indigo-800`;
      case "GRANTED":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "REJECTED":
      case "NOT GRANTED":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "APPROVED":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "PENDING":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-green-100 text-green-800";
      case "Closing Soon":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleViewDetails = (scholarshipId, scholarshipType) => {
    if (!scholarships || !Array.isArray(scholarships)) {
      console.error("Scholarships data not available");
      return;
    }

    const selectedScholarship = scholarships.find(
      (s) => s && s.id === scholarshipId
    );
    if (selectedScholarship) {
      dispatch(setSelectedScholarship(selectedScholarship));
      // No longer dispatching requirements here since they're now part of education programs
    }
    navigate(
      `/educationProgram_scholarship/${scholarshipId}/${scholarshipType}`
    );
  };

  const handleViewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedApplication(null);
  };

  const handleOpenUpdateModal = (application) => {
    setApplicationToUpdate(application);
    setUpdateFormData({
      id: application.id,
      currentLevel: application.currentLevel || "",
      applyLevel: application.applyLevel || "",
      startDate: application.startDate || "",
      endDate: application.endDate || "",
      currency: application.currency || "",
      tuitionFee: application.tuitionFee || 0,
      monthlyLivingAllowance: application.monthlyLivingAllowance || 0,
      researchAllowance: application.researchAllowance || 0,
      airTicketAllowance: application.airTicketAllowance || 0,
      medicalInsurance: application.medicalInsurance || 0,
      coveredPeriod: application.coveredPeriod || 0,
      remainingPeriod: application.remainingPeriod || 0,
      workingPlace: application.workingPlace || "",
      courseSector: application.courseSector || "UN_MENTIONED",
    });
    setIsUpdateModalOpen(true);
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setApplicationToUpdate(null);
    setUpdateFormData({});
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    // Add authentication check
    const userId = getUserId();
    if (!userId) {
      toast.error("Authentication error. Please log in again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    updateFormData.courseSector = updateFormData.courseSector || "UN_MENTIONED";

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-scholarship-application/update-full`,
        updateFormData,
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        }
      );

      if (response.status === 200) {
        toast.success("Application updated successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Refresh the applications list
        const updatedResponse = await axios.get(
          process.env.REACT_APP_BASE_URL +
            `hec/abroad-scholarship-application/get-by-user?userId=${userId}`,
          { headers: getAuthHeaders() }
        );
        setAppliedScholarship(updatedResponse.data || []);

        closeUpdateModal();
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update application. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "GRANTED":
      case "APPROVED":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "REJECTED":
        return <XCircle className="w-5 h-5 text-red-600" />;
      case "UNDER_REVIEW":
      case "UNDER REVIEW":
      case "PENDING":
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case "RECEIVED":
      case "RECEIVED BY HEC":
        return <Clock className="w-5 h-5 text-blue-600" />;
      case "SUBMITTED_TO_PARTENER":
      case "RECEIVED BY PARTNER":
        return <FileText className="w-5 h-5 text-purple-600" />;
      case "SUBMITTED_TO_UNIVERSITY":
      case "RECEIVED BY UNIVERSITY":
        return <GraduationCap className="w-5 h-5 text-indigo-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="p-4 sm:ml-64">
        <div className="p-4 border-2 border-white border-b rounded-lg dark:border-gray-700">
          <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                    <GraduationCap className="w-8 h-8 text-cyan-600" />
                  </div>
                  <p className="text-cyan-600">
                    Loading applied scholarships...
                  </p>
                </div>
              ) : error ? (
                <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                  <p className="font-medium">Error: {error}</p>
                  <p>Please try again later.</p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2 text-cyan-900">
                      Available Abroad Scholarships
                    </h1>
                    <p className="text-cyan-900">
                      Explore our curated list of scholarship opportunities
                      offered by HEC. Each scholarship is designed to support
                      talented individuals in pursuing their academic and
                      professional goals. Review the details below and discover
                      the programs that best match your aspirations.
                    </p>
                  </div>
                  {/* Navigation Tabs */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
                    <div className="border-b border-gray-200">
                      <nav className="flex space-x-8 px-6">
                        <button
                          onClick={() => setActiveTab("applications")}
                          className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === "applications"
                              ? "border-cyan-800 text-cyan-800"
                              : "border-transparent text-gray-500 hover:text-cyan-800 hover:border-cyan-200"
                          }`}
                        >
                          Available Scholarships
                        </button>
                        <button
                          onClick={() => setActiveTab("my-applications")}
                          className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === "my-applications"
                              ? "border-cyan-800 text-cyan-800"
                              : "border-transparent text-gray-500 hover:text-cyan-800 hover:border-cyan-200"
                          }`}
                        >
                          My Applications
                        </button>
                      </nav>
                    </div>
                    {/* Search and Filter */}
                    {activeTab === "applications" ? (
                      <div className="p-6 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              placeholder="Search scholarships by country, level, or keyword..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            />
                          </div>
                          <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <select
                              value={categoryFilter}
                              onChange={(e) =>
                                setCategoryFilter(e.target.value)
                              }
                              className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white min-w-[180px]"
                            >
                              <option value="">All Categories</option>
                              <option value="GOVERNMENT">Government</option>
                              <option value="COOPERATION">Cooperation</option>
                            </select>
                          </div>
                          {(searchTerm || categoryFilter) && (
                            <button
                              onClick={() => {
                                setSearchTerm("");
                                setCategoryFilter("");
                              }}
                              className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                            >
                              <X className="w-5 h-5 text-red-600" />
                              <span className="text-red-600">Clear</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div></div>
                    )}
                    {/* Content */}
                    <div className="p-6">
                      {activeTab === "applications" && (
                        <div>
                          <div className="mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                              Available Abroad Scholarships
                            </h2>
                            <p className="text-gray-600">
                              Choose the scholarship you want to apply for and
                              click 'Apply Now' button.
                            </p>
                          </div>
                          {/* scholarship category */}

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {filteredScholarships &&
                              Array.isArray(filteredScholarships) &&
                              filteredScholarships
                                .map((scholarship) => {
                                  if (!scholarship) return null;
                                  return (
                                    <div
                                      key={
                                        scholarship.id ||
                                        `scholarship-${Math.random()}`
                                      }
                                      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300"
                                    >
                                      <div className="h-32 bg-gradient-to-br from-cyan-800 to-cyan-600 relative overflow-hidden">
                                        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                                        <div className="absolute top-4 left-4">
                                          <span
                                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                              scholarship.status
                                            )}`}
                                          >
                                            {scholarship.status}
                                          </span>
                                        </div>
                                        <div className="absolute top-4 right-4">
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-800">
                                            {scholarship.category}
                                          </span>
                                        </div>{" "}
                                        {/* Category */}
                                        <div className="absolute bottom-4 left-4 right-4">
                                          <div className="flex items-center justify-between text-white">
                                            <div className="flex items-center space-x-2 text-sm">
                                              <MapPin className="w-4 h-4" />
                                              <span className="font-medium">
                                                {scholarship.country}
                                              </span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-sm">
                                              <GraduationCap className="w-4 h-4" />
                                              <span>
                                                {scholarship.programCount}{" "}
                                                {scholarship.programCount === 1
                                                  ? "Program"
                                                  : "Programs"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="p-6">
                                        <div className="mb-4">
                                          <h3 className="font-bold text-gray-900 text-lg mb-2 leading-tight">
                                            {scholarship.title}
                                          </h3>
                                          <div className="flex items-center space-x-2 mb-3">
                                            {/* <MapPin className="w-4 h-4 text-cyan-800" />
                                      <span className="text-sm font-medium text-cyan-800">
                                        {scholarship.country}
                                      </span> */}
                                          </div>
                                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                                            {scholarship.description}
                                          </p>
                                        </div>
                                        <div className="border-t border-gray-100 pt-4 mb-4">
                                          <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                              <span className="text-gray-500">
                                                Application Period:
                                              </span>
                                              <p className="font-medium text-gray-900">
                                                {scholarship.startDate}
                                              </p>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">
                                                Deadline:
                                              </span>
                                              <p className="font-medium text-cyan-800">
                                                {scholarship.deadline}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                        {scholarship.educationPrograms &&
                                          scholarship.educationPrograms.length >
                                            0 && (
                                            <div className="mb-4">
                                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                                Available Programs (
                                                {scholarship.programCount}):
                                              </h4>
                                              <ul className="text-xs text-gray-600 space-y-2">
                                                {scholarship.educationPrograms
                                                  .slice(0, 2)
                                                  .map((program) => (
                                                    <li
                                                      key={program.id}
                                                      className="flex flex-col space-y-1 pb-2 border-b border-gray-100 last:border-0"
                                                    >
                                                      <div className="flex items-start space-x-2">
                                                        <GraduationCap className="w-3.5 h-3.5 text-cyan-600 mt-0.5 flex-shrink-0" />
                                                        <div className="flex-1">
                                                          <span className="font-medium text-gray-800 line-clamp-1">
                                                            {
                                                              program.programName
                                                            }
                                                          </span>
                                                          <div className="text-gray-500 mt-0.5">
                                                            {
                                                              program.universityName
                                                            }{" "}
                                                            •{" "}
                                                            {program.studyYears}{" "}
                                                            years
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </li>
                                                  ))}
                                                {scholarship.educationPrograms
                                                  .length > 2 && (
                                                  <li className="text-cyan-800 font-medium pt-1">
                                                    +
                                                    {scholarship
                                                      .educationPrograms
                                                      .length - 2}{" "}
                                                    more programs...
                                                  </li>
                                                )}
                                              </ul>
                                            </div>
                                          )}
                                        <div className="flex space-x-3">
                                          {(() => {
                                            const today = new Date();
                                            const isWithinApplicationPeriod =
                                              scholarship.rawStartDate &&
                                              scholarship.rawEndDate &&
                                              today >=
                                                scholarship.rawStartDate &&
                                              today <= scholarship.rawEndDate;

                                            const isBeforeStart =
                                              scholarship.rawStartDate &&
                                              today < scholarship.rawStartDate;
                                            const isAfterEnd =
                                              scholarship.rawEndDate &&
                                              today > scholarship.rawEndDate;

                                            return (
                                              <button
                                                onClick={() =>
                                                  isWithinApplicationPeriod &&
                                                  handleViewDetails(
                                                    scholarship.id,
                                                    scholarship.category
                                                  )
                                                }
                                                disabled={
                                                  !isWithinApplicationPeriod
                                                }
                                                className={`flex-1 font-medium py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 ${
                                                  isWithinApplicationPeriod
                                                    ? "bg-cyan-800 hover:bg-cyan-700 text-white focus:ring-cyan-500 cursor-pointer"
                                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                }`}
                                                title={
                                                  isBeforeStart
                                                    ? `Applications open on ${scholarship.startDate}`
                                                    : isAfterEnd
                                                    ? `Applications closed on ${scholarship.deadline}`
                                                    : "View Programs & Apply"
                                                }
                                              >
                                                {isBeforeStart
                                                  ? `Opens ${scholarship.startDate}`
                                                  : isAfterEnd
                                                  ? "Application Closed"
                                                  : "View Programs & Apply"}
                                              </button>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                                .filter(Boolean)}
                          </div>
                          {(!filteredScholarships ||
                            filteredScholarships.length === 0) && (
                            <div className="text-center py-12">
                              <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">
                                No scholarships found
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                Try adjusting your search criteria.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {activeTab === "my-applications" && (
                        <div className="mb-6">
                          <div className="min-h-screen p-6 bg-gray-50">
                            <div className="max-w-7xl mx-auto">
                              {/* Header */}
                              <div className="mb-8">
                                <h1 className="text-3xl font-bold mb-2 text-cyan-900">
                                  Scholarship Applications
                                </h1>
                                <p className="text-cyan-900">
                                  Manage and track your scholarship applications
                                </p>
                              </div>
                              {/* Search Bar */}
                              <div className="bg-white rounded-lg shadow-sm border p-6 mb-6 border-cyan-200">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
                                  <input
                                    type="text"
                                    placeholder="Search by program, university, status, or scholarship..."
                                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 border-cyan-200 text-cyan-900 outline-cyan-900"
                                    value={searchTerm}
                                    onChange={(e) =>
                                      setSearchTerm(e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              {/* Results Counter */}
                              <div className="mb-4">
                                <p className="text-sm text-cyan-900">
                                  Showing {filteredData?.length || 0} of{" "}
                                  {appliedScholarship?.length || 0} applications
                                </p>
                              </div>
                              {/* Table */}
                              <div className="mt-6 bg-white rounded-lg shadow-sm border overflow-hidden border-cyan-200">
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-cyan-900">
                                      <tr>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          Program Details
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          University
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          Education Level
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          Duration
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-white">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-cyan-50">
                                      {filteredData &&
                                        Array.isArray(filteredData) &&
                                        filteredData
                                          .map((item) => {
                                            if (
                                              !item ||
                                              !item.educationProgram
                                            ) {
                                              return null;
                                            }
                                            return (
                                              <tr
                                                key={
                                                  item.id ||
                                                  `application-${Math.random()}`
                                                }
                                                className="hover:bg-cyan-50 transition-colors duration-150"
                                              >
                                                <td className="px-6 py-4">
                                                  <div className="flex items-start space-x-3">
                                                    <div className="flex-shrink-0">
                                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-50">
                                                        <GraduationCap className="w-5 h-5 text-cyan-900" />
                                                      </div>
                                                    </div>
                                                    <div>
                                                      <div className="text-sm font-medium leading-tight text-cyan-900">
                                                        {item.educationProgram
                                                          ?.programName ||
                                                          "Program name not available"}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                  <div className="flex items-center space-x-2">
                                                    <MapPin className="w-4 h-4 text-cyan-400" />
                                                    <div className="text-sm text-cyan-900">
                                                      {item.educationProgram
                                                        ?.universityName ||
                                                        "University not specified"}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                  <div className="text-sm text-cyan-900">
                                                    <div className="flex items-center space-x-1">
                                                      <span>
                                                        {item.currentLevel ||
                                                          "N/A"}
                                                      </span>
                                                      <span className="text-cyan-400">
                                                        →
                                                      </span>
                                                      <span className="font-medium text-cyan-900">
                                                        {item.applyLevel ||
                                                          "N/A"}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                  <div className="flex items-center space-x-2">
                                                    <Calendar className="w-4 h-4 text-cyan-800" />
                                                    <div className="text-sm text-cyan-900">
                                                      <div>
                                                        {item.educationProgram
                                                          ?.studyYears ||
                                                          "N/A"}{" "}
                                                        years
                                                      </div>
                                                      <div className="text-xs text-cyan-900">
                                                        {item.educationProgram
                                                          ?.startDate
                                                          ? formatDate(
                                                              item
                                                                .educationProgram
                                                                .startDate
                                                            )
                                                          : "N/A"}{" "}
                                                        -{" "}
                                                        {item.educationProgram
                                                          ?.endDate
                                                          ? formatDate(
                                                              item
                                                                .educationProgram
                                                                .endDate
                                                            )
                                                          : "N/A"}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                  <span
                                                    className={getStatusBadge(
                                                      item.applicationStatus ||
                                                        "PENDING"
                                                    )}
                                                  >
                                                    {getApplicationStatus(
                                                      item.applicationStatus ||
                                                        "PENDING"
                                                    )}
                                                  </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                  <div className="flex items-center space-x-2">
                                                    <button
                                                      onClick={() =>
                                                        handleViewApplicationDetails(
                                                          item
                                                        )
                                                      }
                                                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-800 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                                                    >
                                                      <Eye className="w-4 h-4" />
                                                      <span>View</span>
                                                    </button>
                                                    {item.applicationStatus ===
                                                      "RECEIVED" && (
                                                      <button
                                                        onClick={() =>
                                                          handleOpenUpdateModal(
                                                            item
                                                          )
                                                        }
                                                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                      >
                                                        <Edit className="w-4 h-4" />
                                                        <span>Update</span>
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })
                                          .filter(Boolean)}
                                    </tbody>
                                  </table>
                                </div>
                                {(!filteredData ||
                                  filteredData.length === 0) && (
                                  <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-cyan-50">
                                      <Search className="w-8 h-8 text-cyan-400" />
                                    </div>
                                    <h3 className="text-lg font-medium mb-2 text-cyan-900">
                                      No results found
                                    </h3>
                                    <p className="text-cyan-400">
                                      Try adjusting your search terms or clear
                                      the search to see all applications.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer />

      {/* Update Application Modal */}
      {isUpdateModalOpen && applicationToUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <Edit className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Update Application
                  </h2>
                  <p className="text-blue-100 text-sm">
                    Modify your scholarship application details
                  </p>
                </div>
              </div>
              <button
                onClick={closeUpdateModal}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <form
              onSubmit={handleUpdateSubmit}
              className="overflow-y-auto max-h-[calc(90vh-160px)]"
            >
              <div className="p-6 space-y-6">
                {/* Program Information (Read-only) */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    <span>Program Information</span>
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Program Name
                        </label>
                        <p className="text-gray-900 font-medium">
                          {applicationToUpdate.educationProgram?.programName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          University
                        </label>
                        <p className="text-gray-900 font-medium">
                          {applicationToUpdate.educationProgram?.universityName}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Editable Fields */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <User className="w-5 h-5 text-blue-600" />
                    <span>Application Details</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Level <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="currentLevel"
                          value={updateFormData.currentLevel}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Apply Level <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="applyLevel"
                          value={updateFormData.applyLevel}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      {updateFormData.priorityCourse && (
                        <>
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date
                        </label>
                        <input
                          type="date"
                          name="startDate"
                          value={updateFormData.startDate}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          name="endDate"
                          value={updateFormData.endDate}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency
                        </label>
                        <input
                          type="text"
                          name="currency"
                          value={updateFormData.currency}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Course Sector
                        </label>
                        <select
                          name="courseSector"
                          value={updateFormData.courseSector}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select your area of study</option>
                          <option value="AVIATION">Aviation</option>
                          {/* <option value="CONSTRUCTION">Construction</option> */}
                          <option value="HEALTH">Health</option>
                          {/* <option value="BUSINESS">Business</option> */}
                          <option value="ICT">ICT</option>
                          {/* <option value="ARTS">Arts</option> */}
                          {/* <option value="SCIENCE">Science</option> */}
                          {/* <option value="LAW">Law</option> */}
                          {/* <option value="EDUCATION">Education</option> */}
                          {/* <option value="AGRICULTURE">Agriculture</option> */}
                          <option value="NUCLEAR_SCIENCE_AND_TECHNOLOGY">
                            Nuclear Science and Technology
                          </option>
                          <option value="INFRASTRUCTURE">Infrastructure</option>
                          <option value="SPORT">Sport</option>
                          {/* <option value="MINING">Mining</option> */}
                          {/* <option value="TOURISM">Tourism</option> */}
                          {/* <option value="ENVIRONMENTAL">Environmental</option> */}
                        </select>
                      </div>
                      </>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Working Place
                        </label>
                        <input
                          type="text"
                          name="workingPlace"
                          value={updateFormData.workingPlace}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                {updateFormData.priorityCourse && (
                <>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span>Financial Details</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tuition Fee
                        </label>
                        <input
                          type="number"
                          name="tuitionFee"
                          value={updateFormData.tuitionFee}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monthly Living Allowance
                        </label>
                        <input
                          type="number"
                          name="monthlyLivingAllowance"
                          value={updateFormData.monthlyLivingAllowance}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Research Allowance
                        </label>
                        <input
                          type="number"
                          name="researchAllowance"
                          value={updateFormData.researchAllowance}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Air Ticket Allowance
                        </label>
                        <input
                          type="number"
                          name="airTicketAllowance"
                          value={updateFormData.airTicketAllowance}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Medical Insurance
                        </label>
                        <input
                          type="number"
                          name="medicalInsurance"
                          value={updateFormData.medicalInsurance}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Study Period */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span>Study Period</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Covered Period (months)
                        </label>
                        <input
                          type="number"
                          name="coveredPeriod"
                          value={updateFormData.coveredPeriod}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Remaining Period (months)
                        </label>
                        <input
                          type="number"
                          name="remainingPeriod"
                          value={updateFormData.remainingPeriod}
                          onChange={handleUpdateInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeUpdateModal}
                  className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Update Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Application Details Modal */}
      {isModalOpen && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-cyan-800 to-cyan-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Application Details
                  </h2>
                  <p className="text-cyan-100 text-sm">
                    Information about your scholarship application
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-6 space-y-6">
                {/* Status Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(selectedApplication.applicationStatus)}
                      <div>
                        <h3 className="text-sm font-medium text-gray-600">
                          Application Status
                        </h3>
                        <p className="text-lg font-bold text-gray-900">
                          {getApplicationStatus(
                            selectedApplication.applicationStatus
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Education Program Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <GraduationCap className="w-5 h-5 text-cyan-800" />
                    <span>Education Program Details</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Program Name
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication.educationProgram?.programName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          University
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication.educationProgram?.universityName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Study Duration
                        </label>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-cyan-600" />
                          <p className="text-gray-900 font-medium">
                            {" "}
                            {selectedApplication?.startDate &&
                              selectedApplication?.endDate && (
                                <span className="ml-2 text-cyan-800 font-medium">
                                  {Math.abs(
                                    new Date(
                                      selectedApplication.endDate
                                    ).getFullYear() -
                                      new Date(
                                        selectedApplication.startDate
                                      ).getFullYear()
                                  )}{" "}
                                  years
                                </span>
                              )}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Study Period
                        </label>
                        <p className="text-gray-900 text-sm">
                          {formatDate(selectedApplication.startDate)} -{" "}
                          {formatDate(selectedApplication.endDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Applicant Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <User className="w-5 h-5 text-cyan-800" />
                    <span>Applicant Information</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Current Level
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.currentLevel || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Applying for Level
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.applyLevel || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Course Name
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.priorityCourse?.courseName ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Course Sector
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.courseSector || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Tuition Fee
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.tuitionFee || "N/A"}{" "}
                          {selectedApplication?.currency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Monthly Living Allowance
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.monthlyLivingAllowance || "0"}{" "}
                          {selectedApplication?.currency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Research Allowance
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.researchAllowance || "0"}{" "}
                          {selectedApplication?.currency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Air Ticket Allowance
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.airTicketAllowance || "0"}{" "}
                          {selectedApplication?.currency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Medical Insurance
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.medicalInsurance || "0"}{" "}
                          {selectedApplication?.currency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Working Place
                        </label>
                        <p className="text-gray-900 font-medium">
                          {selectedApplication?.workingPlace || "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Academic Merit Information */}
                    {selectedApplication?.academicMerit && (
                      <div className="mt-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                          Academic Merit
                        </h4>
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Marks Range
                              </label>
                              <p className="text-gray-900 font-medium">
                                {selectedApplication.academicMerit.marks}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Letter Grade
                              </label>
                              <p className="text-gray-900 font-medium">
                                {selectedApplication.academicMerit.letterGrade}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Scale
                              </label>
                              <p className="text-gray-900 font-medium">
                                {selectedApplication.academicMerit.scale}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Degree Qualification
                              </label>
                              <p className="text-gray-900 font-medium">
                                {
                                  selectedApplication.academicMerit
                                    .degreeQualification
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Study Period Information */}
                    <div className="mt-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">
                        Study Period Details
                      </h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Covered Period
                            </label>
                            <p className="text-gray-900 font-medium">
                              {selectedApplication?.coveredPeriod || "0"} months
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Remaining Period
                            </label>
                            <p className="text-gray-900 font-medium">
                              {selectedApplication?.remainingPeriod || "0"}{" "}
                              months
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Program Requirements */}
                {selectedApplication.educationProgram?.requirements &&
                  selectedApplication.educationProgram.requirements.length >
                    0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-cyan-800" />
                        <span>Program Requirements</span>
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-3">
                          The following requirements were needed for this
                          program:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedApplication.educationProgram.requirements.map(
                            (requirement, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-3 p-3 bg-cyan-50 rounded-lg border border-cyan-100"
                              >
                                <div className="w-6 h-6 bg-cyan-800 rounded-full flex items-center justify-center flex-shrink-0">
                                  <CheckCircle className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {requirement.name}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Submitted Documents */}
                {selectedApplication?.documents &&
                  selectedApplication?.documents?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-cyan-800" />
                        <span>Submitted Documents</span>
                      </h3>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="divide-y divide-gray-200">
                          {selectedApplication?.documents.map((doc, index) => (
                            <div
                              key={index}
                              className="p-4 hover:bg-gray-50 transition-colors duration-150"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-cyan-800" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {doc.documentPath
                                        ? doc.documentPath.split("/").pop()
                                        : `Document ${index + 1}`}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Application Timeline */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-cyan-800" />
                    <span>Application Timeline</span>
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-cyan-800 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Application Submitted
                          </p>
                          <p className="text-xs text-gray-500">
                            {selectedApplication.createdAt
                              ? formatDate(selectedApplication.createdAt)
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 ${
                            selectedApplication.applicationStatus ===
                              "RECEIVED" ||
                            selectedApplication.applicationStatus ===
                              "APPROVED" ||
                            selectedApplication.applicationStatus === "REJECTED"
                              ? "bg-cyan-800"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Application Received
                          </p>
                          <p className="text-xs text-gray-500">
                            Under review by HEC
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 ${
                            selectedApplication.applicationStatus ===
                              "APPROVED" ||
                            selectedApplication.applicationStatus === "REJECTED"
                              ? "bg-cyan-800"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Decision Made
                          </p>
                          <p className="text-xs text-gray-500">
                            {selectedApplication.applicationStatus ===
                            "APPROVED"
                              ? "Your application has been approved"
                              : selectedApplication.applicationStatus ===
                                "REJECTED"
                              ? "Your application was not successful"
                              : "Pending decision"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AbroadScholarship;
